import React from "react";

import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import Toolbar from "@mui/material/Toolbar";

import PageOption from "./../PageTypes/PageOption";

import NotStartedOutlinedIcon from "@mui/icons-material/NotStartedOutlined";
import SsidChartOutlinedIcon from "@mui/icons-material/SsidChartOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import SpeedIcon from "@mui/icons-material/Speed";
import SignpostOutlinedIcon from "@mui/icons-material/SignpostOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import PercentIcon from "@mui/icons-material/Percent";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DoNotDisturbOnTotalSilenceIcon from "@mui/icons-material/DoNotDisturbOnTotalSilence";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import TourOutlinedIcon from "@mui/icons-material/TourOutlined";
import { isMobile } from "react-device-detect";

import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
/*
  NavigationRoot Component is the main component for managing page navigation
*/

type NavigationRootProps = {
  currentPage: PageOption;
  mobileNavigationOpen: boolean; // This is only used on mobile
  setMobileNavigationOpen: (newOpen: boolean) => void; // This is only used on mobile
  navPageOptions: PageOption[];
  makeNavRootLabel: (p: PageOption) => string;
  disabledList: PageOption[];
  disabledPagesListSandbox: PageOption[];
  isDevPage: (p: PageOption) => boolean;
  onChange: (newPage: PageOption) => void;
  userFullName: string | undefined;
  openLogout: () => void;
  width: number;
  stillDoingFirstAuth: boolean;
  tourOff: boolean;
  enableTour: () => void;
  disableTour: () => void;
  extraMenuOptions: ({ text: string; icon: any; action: () => void } | { divider: boolean })[];
};

function NavigationRoot(props: NavigationRootProps) {
  const theme = useTheme();

  const [userFullNameAnchorElement, setUserFullNameAnchorElement] = React.useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userFullNameAnchorElement);
  const handleUserMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setUserFullNameAnchorElement(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserFullNameAnchorElement(null);
  };

  const contactUsDetails = {
    text: "Contact",
    icon: <EmailOutlinedIcon />,
    action: () =>
      window.open("mailto:contact@usesalus.com?subject=[Site Inquiry] " + (props.userFullName || "New User"), "_blank"),
  };

  const pageIconMap: {
    [key in PageOption]: any;
  } = {
    onboarding: <NotStartedOutlinedIcon />,
    overview: <SsidChartOutlinedIcon />,
    "stock-plans": <TableRowsOutlinedIcon />,
    espps: <DiscountOutlinedIcon />,
    "espp-explorer": <TuneIcon />,
    forecasts: <SpeedIcon />,
    strategies: <SignpostOutlinedIcon />,
    trade: <SyncAltIcon />,
    tax: <PercentIcon />,
    learn: <SchoolOutlinedIcon />,
    plaid: <DoNotDisturbOnTotalSilenceIcon />,
    profile: <AccountCircleIcon />,
    theme: <DoNotDisturbOnTotalSilenceIcon />,
    form: <DoNotDisturbOnTotalSilenceIcon />,
  };

  const pageList = (
    <List sx={{ flexGrow: 1 }}>
      {props.navPageOptions.map((text, index) => {
        const disabled = props.disabledList.includes(text);
        const sandboxDisabled = props.disabledPagesListSandbox.includes(text);
        const contents = (
          <ListItem key={text} disablePadding>
            <ListItemButton
              onClick={(e) => {
                props.onChange(text);
                props.setMobileNavigationOpen(false);
              }}
              selected={props.currentPage === text}
              disableGutters
              disabled={disabled || sandboxDisabled}
            >
              <ListItemIcon sx={{ minWidth: "0px", paddingLeft: 1, paddingRight: 1 }}>{pageIconMap[text]}</ListItemIcon>
              <ListItemText primary={(props.isDevPage(text) ? "[DEV] " : "") + props.makeNavRootLabel(text)} />
            </ListItemButton>
          </ListItem>
        );
        if (disabled) {
          return (
            <Tooltip key={text} title="Coming Soon" arrow placement="right">
              {contents}
            </Tooltip>
          );
        } else if (sandboxDisabled) {
          return (
            <Tooltip key={text} title="This module is not available in this sandbox" arrow placement="right">
              {contents}
            </Tooltip>
          );
        } else {
          return contents;
        }
      })}
    </List>
  );

  const profilePopupMenuItems = [
    ...props.extraMenuOptions,
    {
      text: "My Profile",
      icon: <AccountCircleIcon />,
      action: () => props.onChange("profile"),
    },
    {
      text: "Settings",
      icon: <SettingsOutlinedIcon />,
      action: () => props.onChange("profile"),
    },
    { divider: true },
    {
      text: props.tourOff ? "Enable Tour" : "Disable Tour",
      icon: <TourOutlinedIcon />,
      action: () => {
        if (props.tourOff) {
          props.enableTour();
        } else {
          props.disableTour();
        }
      },
    },
    { divider: true },
    { text: "Log Out", icon: <LogoutOutlinedIcon />, action: props.openLogout },
    contactUsDetails,
  ];
  const profilePopupMenu = (
    <Menu
      id="basic-menu"
      anchorEl={userFullNameAnchorElement}
      open={userMenuOpen}
      onClose={handleUserMenuClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      {profilePopupMenuItems.map((button_details, index) =>
        "divider" in button_details ? (
          <Divider key={index} />
        ) : (
          <MenuItem
            key={button_details.text}
            onClick={() => {
              button_details.action();
              handleUserMenuClose();
              props.setMobileNavigationOpen(false);
            }}
          >
            <ListItemIcon sx={{ minWidth: "0px", paddingLeft: 0, paddingRight: 1 }}>{button_details.icon}</ListItemIcon>
            <ListItemText primary={button_details.text} />
          </MenuItem>
        )
      )}
    </Menu>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={props.mobileNavigationOpen}
      onClose={isMobile ? () => props.setMobileNavigationOpen(false) : undefined}
      sx={{
        width: props.width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        [`& .MuiDrawer-paper`]: {
          width: props.width,
          boxSizing: "border-box",
          // @ts-expect-error
          backgroundColor: theme.palette.background.dark,
        },
      }}
    >
      <Toolbar variant="dense" sx={{ flexGrow: 0 }} />
      {props.stillDoingFirstAuth ? null : (
        <>
          {props.userFullName === undefined
            ? null // doesn't present the page options if the user is not logged in
            : pageList}
          <List
            sx={{
              flexGrow: props.userFullName === undefined ? 1 : 0,
            }}
          >
            <ListItem key="Auth Dialog" disablePadding>
              <ListItemButton onClick={handleUserMenuClick} disableGutters>
                <ListItemIcon sx={{ minWidth: "0px", paddingLeft: 1, paddingRight: 1 }}>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText primary={props.userFullName} />
              </ListItemButton>
            </ListItem>
          </List>
          {profilePopupMenu}
        </>
      )}
    </Drawer>
  );
}

export default NavigationRoot;
